import * as React from 'react';
import { useProjMsg } from './hooks';
import LocalHTMLContainer from '../../localization/LocalHTMLContainer';
import { removeRedundantBackslashes } from '../../localization/removeRedundantBackslashes';
import { useAppState } from '../../core/redux/hooks';


type SpanOrDiv = Extract<HTMLDivElement, HTMLSpanElement>;
type SpanOrDivProps = React.DetailedHTMLProps<React.HTMLAttributes<SpanOrDiv>, SpanOrDiv>;


interface Props extends SpanOrDivProps {
  str: string | number;
  defaultMsg?: string | number;
  curlyTokens?: { [key: string]: string | number | undefined; };
  pointyTokens?: { [key: string]: string | number | undefined; };
}


/**
 * LocalMsgContainer: a component that renders a translated message as either plain text or, if it contains, markup,
 * raw HTML.
 *
 * When replacing with 'curlyTokens', this component automatically adds interpolation braces to your keys
 * -- use `userName` to replace `{userName}` from the raw message text.
 *
 * When replacing with `pointyTokens`, this component automatically adds TruScore-specific tag boundaries to your keys
 * -- use `userName` to replace `<@userName>` from the raw message text.
 *
 * Props:
 * 1. The `msgKey` prop is the key for the translated message object stored in app state.
 * 1. The `defaultMsg` prop allows the user the opportunity to pass in a default string if one is preferred over the
 * fallback string. If set to the empty string, when a translated message is missing, no component will be rendered.
 * Note that this prop is ignored if a translation exists and contains HTML markup.
 * 1. The `curlyTokens` and `pointyTokens` props are both objects of `{'toReplace': 'replacement'}` key-value pairs.
 * It's recommended that you memoize this object in your component, otherwise, replacement logic will run on every
 * render XP
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const ProjMsg: React.FC<Props> = props => {
  const { str, defaultMsg, curlyTokens, pointyTokens, ...rest } = props;

  const msg     = useProjMsg(props.str);

  const msgText = React.useMemo(
    () => {
      const text = msg?.text ?? defaultMsg;

      if (!text) {
        return;
      }

      const withTokensReplaced = curlyTokens
        ? Object
          .keys(curlyTokens!)
          .reduce<string>(
            (running, next) => running.replace(new RegExp('{' + next + '}', 'g'), `${curlyTokens![next] ?? ''}`),
            `${text}`,
          )
        : text;

      return pointyTokens
        ? Object
          .keys(pointyTokens!)
          .reduce<string>(
            (running, next) => running.replace(new RegExp('<@' + next + '>', 'g'), `${pointyTokens![next] ?? ''}`),
            `${withTokensReplaced}`,
          )
        : withTokensReplaced;
    },
    [msg, defaultMsg, curlyTokens, pointyTokens],
  );
  
  const dir = useAppState(state => state.locale.textDirection);

  if (!msgText) {
    return null;
  }

  return msg && msg.containsMarkup
    ? <LocalHTMLContainer {...rest} innerHtml={removeRedundantBackslashes(`${msgText}`)} />
    : <span {...rest} dir={dir}>{msgText ?? defaultMsg}</span>;
};

export default ProjMsg;
